// Client-side prompt building utility that matches server logic exactly

export const buildFullPromptForAdmin = async (basePrompt, spiceIds = []) => {
  try {
    console.log('[PROMPT-BUILDER] Starting reconstruction:', { basePrompt, spiceIds });
    
    if (!spiceIds || spiceIds.length === 0) {
      console.log('[PROMPT-BUILDER] No spices provided, returning base prompt');
      return basePrompt;
    }

    // Fetch the current spice config from the server
    const apiUrl = process.env.REACT_APP_API_URL || '';
    console.log('[PROMPT-BUILDER] Fetching config from:', `${apiUrl}/api/stylekit`);
    
    const response = await fetch(`${apiUrl}/api/stylekit`);
    if (!response.ok) {
      console.warn('[PROMPT-BUILDER] Failed to fetch spice config, status:', response.status);
      return basePrompt;
    }
    
    const config = await response.json();
    console.log('[PROMPT-BUILDER] Fetched config:', config);

    // Group spices by category to respect combine types - matches server logic exactly
    const spicesByCategory = {};
    
    // Search through all spice categories and group by category
    Object.entries(config).forEach(([categoryKey, category]) => {
      if (category.items && Array.isArray(category.items)) {
        category.items.forEach(item => {
          if (spiceIds.includes(item.id)) {
            console.log('[PROMPT-BUILDER] Found spice match:', { 
              spiceId: item.id, 
              categoryKey, 
              prompt: item.prompt,
              combineType: category.combineType
            });
            if (!spicesByCategory[categoryKey]) {
              spicesByCategory[categoryKey] = {
                category,
                prompts: []
              };
            }
            spicesByCategory[categoryKey].prompts.push(item.prompt);
          }
        });
      }
    });

    console.log('[PROMPT-BUILDER] Spices grouped by category:', spicesByCategory);

    // If no spices found, return base prompt
    if (Object.keys(spicesByCategory).length === 0) {
      console.log('[PROMPT-BUILDER] No matching spices found, returning base prompt');
      return basePrompt;
    }

    // Build prompt using new structure with order array and userPromptWrapper
    let finalPrompt = '';
    
    // Add prefix if available
    if (config.basePromptStructure?.prefix) {
      finalPrompt = config.basePromptStructure.prefix;
      console.log('[PROMPT-BUILDER] Added prefix:', config.basePromptStructure.prefix);
    }
    
    // Use the new order array from config, fallback to old order for compatibility
    const categoryOrder = config.basePromptStructure?.order || ['styles', 'descriptors', 'vibes', 'lens', 'time', 'film'];
    console.log('[PROMPT-BUILDER] Using category order:', categoryOrder);
    
    categoryOrder.forEach(categoryKey => {
      console.log(`[PROMPT-BUILDER] Processing category: ${categoryKey}`);
      if (categoryKey === 'userPrompt') {
        // Handle user prompt with wrapper
        if (basePrompt) {
          const wrapper = config.basePromptStructure?.userPromptWrapper || '{userPrompt}';
          const wrappedPrompt = wrapper.replace('{userPrompt}', basePrompt);
          // Ensure proper spacing - add space before user prompt if needed
          const needsSpace = finalPrompt && !finalPrompt.endsWith(' ');
          const beforeUserPrompt = finalPrompt;
          finalPrompt += (needsSpace ? ' ' : '') + wrappedPrompt;
          console.log('[PROMPT-BUILDER] Added wrapped user prompt:', {
            before: beforeUserPrompt,
            userPrompt: wrappedPrompt,
            after: finalPrompt
          });
        }
      } else if (spicesByCategory[categoryKey]) {
        const { category, prompts } = spicesByCategory[categoryKey];
        if (prompts.length > 0) {
          console.log('[PROMPT-BUILDER] Processing category:', {
            categoryKey,
            combineType: category.combineType,
            prompts
          });
          
          let addition = '';
          const needsComma = finalPrompt && !finalPrompt.endsWith(' ');
          
          if (category.combineType === 'designers') {
            addition = `${needsComma ? ',' : ''} designed by ${prompts.join(' and ')}`;
          } else if (category.combineType === 'photographers') {
            addition = `${needsComma ? ',' : ''} photographed by ${prompts.join(' and ')}`;
          } else if (category.combineType === 'adjectives') {
            addition = `${needsComma ? ' ' : ''}${prompts.join(', ')}`;
          } else {
            // 'separate' or default
            addition = prompts.map(prompt => 
              prompt.startsWith(',') ? prompt : `${needsComma ? '' : ' '}${prompt}`
            ).join('');
          }
          
          finalPrompt += addition;
          console.log('[PROMPT-BUILDER] Added category:', addition);
        }
      }
    });
    
    // Add suffix if available
    if (config.basePromptStructure?.suffix) {
      finalPrompt += config.basePromptStructure.suffix;
      console.log('[PROMPT-BUILDER] Added suffix:', config.basePromptStructure.suffix);
    }
    
    console.log('[PROMPT-BUILDER] Final reconstructed prompt:', finalPrompt);
    
    return finalPrompt;
  } catch (error) {
    console.error('[PROMPT-BUILDER] Error building prompt with spices:', error);
    // Fallback to base prompt if spice building fails
    return basePrompt;
  }
};

// Enhanced version that respects category structure and base prompt structure
export const buildEnhancedPromptForAdmin = async (basePrompt, spiceIds = []) => {
  try {
    if (!spiceIds || spiceIds.length === 0) {
      return basePrompt;
    }

    const response = await fetch('/api/stylekit');
    if (!response.ok) {
      return basePrompt;
    }
    
    const config = await response.json();
    
    // Group spices by category to respect the intended structure
    const spicesByCategory = {};
    Object.entries(config).forEach(([categoryKey, category]) => {
      if (category.items && Array.isArray(category.items)) {
        category.items.forEach(item => {
          if (spiceIds.includes(item.id)) {
            if (!spicesByCategory[categoryKey]) {
              spicesByCategory[categoryKey] = {
                category,
                prompts: []
              };
            }
            spicesByCategory[categoryKey].prompts.push(item.prompt);
          }
        });
      }
    });

    // Build prompt using new structure with order array and userPromptWrapper
    let enhancedPrompt = '';
    
    // Add prefix if available
    if (config.basePromptStructure?.prefix) {
      enhancedPrompt = config.basePromptStructure.prefix;
    }
    
    // Use the new order array from config, fallback to old order for compatibility
    const categoryOrder = config.basePromptStructure?.order || ['styles', 'descriptors', 'vibes', 'lens', 'time', 'film'];
    
    categoryOrder.forEach(categoryKey => {
      if (categoryKey === 'userPrompt') {
        // Handle user prompt with wrapper
        if (basePrompt) {
          const wrapper = config.basePromptStructure?.userPromptWrapper || '{userPrompt}';
          const wrappedPrompt = wrapper.replace('{userPrompt}', basePrompt);
          // Ensure proper spacing - add space before user prompt if needed
          const needsSpace = enhancedPrompt && !enhancedPrompt.endsWith(' ');
          enhancedPrompt += (needsSpace ? ' ' : '') + wrappedPrompt;
        }
      } else if (spicesByCategory[categoryKey]) {
        const { category, prompts } = spicesByCategory[categoryKey];
        if (prompts.length > 0) {
          let addition = '';
          const needsComma = enhancedPrompt && !enhancedPrompt.endsWith(' ');
          
          if (category.combineType === 'designers') {
            addition = `${needsComma ? ',' : ''} designed by ${prompts.join(' and ')}`;
          } else if (category.combineType === 'photographers') {
            addition = `${needsComma ? ',' : ''} photographed by ${prompts.join(' and ')}`;
          } else if (category.combineType === 'adjectives') {
            addition = `${needsComma ? ' ' : ''}${prompts.join(', ')}`;
          } else {
            // 'separate' or default
            addition = prompts.map(prompt => 
              prompt.startsWith(',') ? prompt : `${needsComma ? '' : ' '}${prompt}`
            ).join('');
          }
          enhancedPrompt += addition;
        }
      }
    });
    
    // Add suffix if available
    if (config.basePromptStructure?.suffix) {
      enhancedPrompt += config.basePromptStructure.suffix;
    }
    
    console.log('[PROMPT-BUILDER] Enhanced prompt reconstruction:', {
      basePrompt,
      spiceIds,
      spicesByCategory,
      enhancedPrompt
    });
    
    return enhancedPrompt;
    
  } catch (error) {
    console.error('[PROMPT-BUILDER] Error building enhanced prompt:', error);
    return basePrompt;
  }
}; 